import { useEffect, useMemo, useRef, useState } from "react";
import { rafThrottle, startTilt, NO_SENSOR_HELP } from "../lib/tilt";
import { Sparkles, Power } from "lucide-react";
import { toast } from "sonner";

const Y = "#FACC15", G = "#10B981", A = "#F59E0B", R = "#EF4444";
const HISTORY = 30;

// On-device analysis of pitch/roll + stability -> verdict and tip
export function analyzeLevel(pitch, roll, jitter) {
  const off = Math.max(Math.abs(pitch), Math.abs(roll));
  if (jitter > 0.6) return { color: A, verdict: "Hold steady", tip: "Readings are shaking — rest the phone on a solid, flat surface." };
  if (off <= 0.3) return { color: G, verdict: "Level · ready to measure", tip: "Surface is within ±0.3°. Readings will be accurate." };
  const axis = Math.abs(pitch) >= Math.abs(roll)
    ? (pitch > 0 ? "front is high — raise the rear or move to flatter ground" : "rear is high — raise the front or move to flatter ground")
    : (roll > 0 ? "right side is high — shim the left or reposition" : "left side is high — shim the right or reposition");
  if (off <= 1) return { color: A, verdict: "Almost level", tip: `Slight tilt: ${axis}. Zero the gauge to compensate.` };
  return { color: R, verdict: "Not level", tip: `${off.toFixed(1)}° off: ${axis}. Measurements will be skewed.` };
}

const Bubble = ({ pitch, roll, color }) => {
  const c = (v) => Math.max(-1, Math.min(1, v / 5)) * 30;
  return (
    <svg width="76" height="76" viewBox="0 0 76 76" className="shrink-0" data-testid="ai-level-bubble">
      <circle cx="38" cy="38" r="34" fill="#070B14" stroke={Y} strokeOpacity="0.5" strokeWidth="2" />
      <circle cx="38" cy="38" r="10" fill="none" stroke="#334155" strokeWidth="1.5" />
      <line x1="4" y1="38" x2="72" y2="38" stroke="#243049" /><line x1="38" y1="4" x2="38" y2="72" stroke="#243049" />
      <circle cx={38 + c(roll)} cy={38 + c(pitch)} r="7" fill={color} />
    </svg>
  );
};

export const AILevel = ({ paused = false }) => {
  const [on, setOn] = useState(false);
  const [data, setData] = useState(null);
  const hist = useRef([]);
  const zero = useRef({ p: 0, r: 0 });
  const stopTiltRef = useRef(null);

  const onOrient = useMemo(() => rafThrottle((e) => {
    if (e.beta == null || e.gamma == null) return;
    const h = hist.current;
    h.push({ p: e.beta, r: e.gamma });
    if (h.length > HISTORY) h.shift();
    const avg = (k) => h.reduce((s, x) => s + x[k], 0) / h.length;
    const p = avg("p"), r = avg("r");
    const jitter = Math.sqrt(h.reduce((s, x) => s + (x.p - p) ** 2 + (x.r - r) ** 2, 0) / h.length);
    const q = (v) => Math.round(v * 20) / 20;
    const next = { pitch: q(p - zero.current.p), roll: q(r - zero.current.r), jitter: q(jitter) };
    setData((d) => (d && d.pitch === next.pitch && d.roll === next.roll && d.jitter === next.jitter ? d : next));
  }), []);

  useEffect(() => () => { stopTiltRef.current?.(); onOrient.cancel(); }, [onOrient]);

  // Pause while another sensor mode (live lean) has the phone on the tire
  useEffect(() => {
    if (!paused || !on) return;
    stopTiltRef.current?.(); stopTiltRef.current = null;
    onOrient.cancel();
    setOn(false); setData(null); hist.current = [];
  }, [paused, on, onOrient]);

  const toggle = async () => {
    if (on) {
      stopTiltRef.current?.(); stopTiltRef.current = null;
      onOrient.cancel();
      setOn(false); setData(null); hist.current = [];
      return;
    }
    try {
      if (typeof DeviceOrientationEvent === "undefined") { toast.error("Motion sensor not available on this device"); return; }
      if (typeof DeviceOrientationEvent.requestPermission === "function") {
        if ((await DeviceOrientationEvent.requestPermission()) !== "granted") { toast.error("Motion sensor permission denied"); return; }
      }
      stopTiltRef.current?.();
      stopTiltRef.current = startTilt(onOrient, () => toast.error(NO_SENSOR_HELP.title, NO_SENSOR_HELP));
      setOn(true);
    } catch { toast.error("Could not start the motion sensor"); }
  };

  const calibrate = () => {
    if (!data) return;
    zero.current = { p: zero.current.p + data.pitch, r: zero.current.r + data.roll };
    toast.success("AI Level zeroed");
  };

  const a = data ? analyzeLevel(data.pitch, data.roll, data.jitter) : null;
  const fmt = (v) => `${v >= 0 ? "+" : ""}${v.toFixed(1)}°`;

  return (
    <div data-testid="ai-level-panel" className="mb-5 rounded-2xl border border-yellow-400/30 bg-yellow-400/5 p-3 flex items-center gap-3">
      <Bubble pitch={data?.pitch ?? 0} roll={data?.roll ?? 0} color={a?.color ?? "#64748B"} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-yellow-400">
          <Sparkles size={12} /> AI Level
        </div>
        {a ? (
          <>
            <div data-testid="ai-level-verdict" className="font-heading text-sm font-bold" style={{ color: a.color }}>{a.verdict}</div>
            <div data-testid="ai-level-readout" className="font-mono text-[10px] text-slate-400 tabular-nums">Pitch {fmt(data.pitch)} · Roll {fmt(data.roll)}</div>
            <div data-testid="ai-level-tip" className="font-body text-[11px] text-slate-300 leading-snug mt-0.5">{a.tip}</div>
          </>
        ) : (
          <div className="font-body text-[11px] text-slate-400 leading-snug mt-0.5">
            {paused ? "Paused while Live Lean is measuring a wheel. Turn Live Lean off to check the ground." : on ? "Waiting for sensor data…" : "Lay the phone flat on the floor or rocker to check the surface is level before measuring."}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1.5 shrink-0">
        <button data-testid="ai-level-toggle" onClick={toggle} disabled={paused} aria-label="Toggle AI level"
          className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${on ? "bg-yellow-400 text-ink border-yellow-400" : "border-yellow-400/40 text-yellow-400 hover:bg-yellow-400/10"} disabled:opacity-40`}>
          <Power size={15} />
        </button>
        {on && (
          <button data-testid="ai-level-zero" onClick={calibrate} disabled={!data}
            className="font-mono text-[9px] uppercase tracking-widest text-yellow-400 hover:text-white disabled:opacity-40">Zero</button>
        )}
      </div>
    </div>
  );
};
